import { flow, getEnv, Instance, types } from "mobx-state-tree";

import { api } from "../api/ApiService";
import { snackbarNotificationsService } from "../notifications/SnackbarNotificationsService";
import { globalModule } from "../service-provider/GlobalModule";
import { PubSubEvents } from "../PubSubEvents";
import { endpoints } from "../api/ApiEndpoints";

interface DownloadSewerObjectsRequest {
  projectId?: number;
  jobId?: number;
  sewerObjectIds: number[];
  includeInspectionAndDamages?: boolean;
  includeMediaFiles?: boolean;
}

export const ExportSewerObjectsModel = types
  .model({
    modalIsVisible: types.boolean,
    downloadProgressSnackbarIsVisible: types.boolean,
    progress: types.number,
    sewerObjectsCount: types.number,
    fileName: types.string,
    sewerObjectsExportApiUrl: types.string,
  })
  .actions((self) => ({
    showModal() {
      self.modalIsVisible = true;
    },
    hideModal() {
      self.modalIsVisible = false;
    },
    showDownloadProgressSnackbar() {
      self.downloadProgressSnackbarIsVisible = true;
    },
    hideDownloadProgressSnackbar() {
      self.downloadProgressSnackbarIsVisible = false;
    },
    updateProgress(progress: number) {
      self.progress = progress;
    },
    setSewerObjectsCount(count: number) {
      self.sewerObjectsCount = count;
    },
    setFileName(fileName: string) {
      self.fileName = fileName;
    },
    resetProgress() {
      self.progress = 0;
    },
    resetSewerObjectsCount() {
      self.sewerObjectsCount = 0;
    },
  }))
  .actions((self) => ({
    startDownload(sewerObjectsCount: number, fileName: string) {
      self.hideModal();
      self.showDownloadProgressSnackbar();
      self.setSewerObjectsCount(sewerObjectsCount);
      self.setFileName(fileName);
    },
    downloadEnded() {
      self.hideDownloadProgressSnackbar();
      self.resetProgress();
      self.resetSewerObjectsCount();
    },
    downloadItemVisibleInBrowserBar() {
      self.updateProgress(99);
    },
  }))
  .actions((self) => {
    const subscribeForDownloadingItemIsVisibleInBrowser = (): string => {
      const downloadItemVisibleSubscribeToken = getEnv(self).PubSub.subscribe(
        `${PubSubEvents.DownloadingItemIsVisibleInBrowser}-${self.sewerObjectsExportApiUrl}`,
        () => {
          self.downloadItemVisibleInBrowserBar();
          setTimeout(() => {
            self.hideDownloadProgressSnackbar();
          }, 300);
        }
      );
      return downloadItemVisibleSubscribeToken;
    };
    const unsubscribeForDownloadingItemIsVisibleInBrowser = (subscribeToken: string) => {
      getEnv(self).PubSub.unsubscribe(subscribeToken);
    };
    return {
      exportSewerObjects: flow(function* ({
        sewerObjectsCount,
        fileName,
        projectId,
        jobId,
        sewerObjectIds,
        includeInspectionAndDamages,
        includeMediaFiles,
      }: {
        sewerObjectsCount: number;
        fileName: string;
        projectId?: number;
        jobId?: number;
        sewerObjectIds: number[];
        includeInspectionAndDamages: boolean;
        includeMediaFiles: boolean;
      }) {
        const endpoint = endpoints.get("sewer_objects_export");
        self.sewerObjectsExportApiUrl = `${endpoint.url}?${endpoint.queryParams}`;
        var subscribeToken = subscribeForDownloadingItemIsVisibleInBrowser();

        const body: DownloadSewerObjectsRequest = {
          projectId,
          jobId,
          sewerObjectIds,
          includeInspectionAndDamages,
          includeMediaFiles,
        };

        try {
          self.startDownload(sewerObjectsCount, fileName);
          yield getEnv(self).api.postDownloadToDisk(endpoint, `${fileName}.zip`, body);

          setTimeout(() => {
            self.downloadEnded();
            snackbarNotificationsService.showSuccess(
              "sewer-objects.DownloadSewerObjectsModal.success",
              { sewerObjectsCount }
            );
          }, 500);
        } catch (error) {
          snackbarNotificationsService.showError("default-error.sewer-objects.download");
          self.downloadEnded();
        } finally {
          unsubscribeForDownloadingItemIsVisibleInBrowser(subscribeToken);
        }
      }),
    };
  });

export type ExportSewerObjectsService = Instance<typeof ExportSewerObjectsModel>;
export const exportSewerObjectsService = ExportSewerObjectsModel.create(
  {
    progress: 0,
    modalIsVisible: false,
    downloadProgressSnackbarIsVisible: false,
    sewerObjectsCount: 0,
    fileName: "files",
    sewerObjectsExportApiUrl: "",
  },
  {
    api,
    PubSub,
  }
);
globalModule.exportSewerObjectsService = exportSewerObjectsService;
